import React from 'react';
import { resolveEstimateDescriptionInline } from '../../utils/estimate-description';

interface OrderDescriptionProps {
    isDraperyOrder: boolean;
    salesDescriptionSettingsMapped: { id: string; isEnabled: boolean }[];
    draperyItemDetail?: {
        pleatTypesWithPricing: { id: any; name: string }[];
    }[];
    data: any;
    editMode: boolean;
}

export const OrderDescription: React.FC<OrderDescriptionProps> = ({
    isDraperyOrder,
    salesDescriptionSettingsMapped,
    draperyItemDetail,
    data,
    editMode
}) => {

    // const showingEditMode = editMode || data.draperyOrderDesign.id === 0 || !data.draperyOrderDesign.id

    const formatPleatType = (pleatType: any) => {
        let formattedPleatType = pleatType.replace(/\d{4}$/, "");

        formattedPleatType = formattedPleatType.replace(/_/g, " ");

        formattedPleatType = formattedPleatType.replace(
            /\b\w/g,
            (char: string) => char.toUpperCase()
        );
        return formattedPleatType;
    };

    const getPleatTypeName = () => {
        const design = data.draperyOrderDesign;
        const ripplefoldOptions = design && design.ripplefoldOptions;

        if (design && design.headingType && design.headingType === "ripplefold") {
            let nameParts = [];

            const masterCarrier = ripplefoldOptions && ripplefoldOptions.masterCarrier;
            if (masterCarrier === "butt") {
                nameParts.push("Butt");
            } else if (masterCarrier === "overlap") {
                nameParts.push("Overlap");
            }

            const mount = ripplefoldOptions && ripplefoldOptions.mount;
            if (mount) {
                const formattedMount = mount.charAt(0).toUpperCase() + mount.slice(1);
                nameParts.push(formattedMount);
            }

            return nameParts.join(" / ");
        }

        if (draperyItemDetail && draperyItemDetail.length > 0 && draperyItemDetail[0] && draperyItemDetail[0].pleatTypesWithPricing) {
            return formatPleatType(data.draperyOrderDesign.pleatType.toLowerCase());
        }

        return "";
    };

    const getControlInfo = () => {
        const design = data.draperyOrderDesign;
        const ripplefoldOptions = design && design.ripplefoldOptions;

        if (design && design.headingType && design.headingType === "ripplefold") {
            let nameParts = [];

            const controlType = ripplefoldOptions && ripplefoldOptions.controlType
            const controlLocation = ripplefoldOptions && ripplefoldOptions.controlLocation

            if (controlType) {
                const formatControlType = controlType.charAt(0).toUpperCase() + controlType.slice(1)
                nameParts.push(formatControlType)
            }

            if (controlLocation) {
                const formatControlLocation = controlLocation.charAt(0).toUpperCase() + controlLocation.slice(1)
                nameParts.push(formatControlLocation)
            }

            return nameParts.join(" - ");
        }

        return;
    };

    const panelValues = Object.values(data.draperyOrderDesign.panelJson || {})
        .filter(value => value !== undefined);

    const panelTypeDisplayMap = {
        'center': 'Center Panel',
        'left': 'Left Panel',
        'right': 'Right Panel',
        'pair': 'Pair',
        'three-panels': '3 Panels',
        'four-panels': '4 Panels',
        'five-panels': '5 Panels'
    };

    type PanelKey = keyof typeof panelTypeDisplayMap;

    const panelType: PanelKey = data.draperyOrderDesign.panelType

    const displayPanelType = panelTypeDisplayMap[panelType] || '';
    // const isSettingEnabled = (id: string) =>
    //     salesDescriptionSettingsMapped.some(item => item.id === id && item.isEnabled);

    const rodWidth = data && data.draperyOrderDesign && data.draperyOrderDesign.rodWidth
    const returnSize = data && data.draperyOrderDesign && data.draperyOrderDesign.customReturnOverlapJson && data.draperyOrderDesign.customReturnOverlapJson.returnSize

    const draperyFullnessValue = data && data.draperyOrderDesign && data.draperyOrderDesign.draperyFullness
    let draperyFullness
    if (data.draperyOrderDesign.headingType === "ripplefold") {
        draperyFullness = parseFloat(draperyFullnessValue) * 100
        draperyFullness = `${draperyFullness}%`
    } else {
        draperyFullness = `${draperyFullnessValue}x`
    }

    const liningPatternItems = data.liningsPatternItems || [];
    const liningItem = data.liningsList.find((l: any) => l.name === "Lining" && l.active)
    const liningOrderOrigin = liningItem && liningItem.draperyOrderLiningsFabric && liningItem.draperyOrderLiningsFabric.orderOrigin
    const liningItemName = resolveEstimateDescriptionInline({
        fabric: liningItem && liningItem.draperyOrderLiningsFabric,
        items: liningPatternItems,
        fallbackMode: "pattern-only",
    });
    const liningYardsNeeded = liningItem && liningItem.draperyOrderLiningsFabric && liningItem.draperyOrderLiningsFabric.yardsNeeded

    const interliningItem = data.liningsList.find((l: any) => l.name === "Interlining" && l.active)
    const interliningOrderOrigin = interliningItem && interliningItem.draperyOrderLiningsFabric && interliningItem.draperyOrderLiningsFabric.orderOrigin
    const interliningItemName = resolveEstimateDescriptionInline({
        fabric: interliningItem && interliningItem.draperyOrderLiningsFabric,
        items: liningPatternItems,
        fallbackMode: "pattern-only",
    });
    const interliningYardsNeeded = interliningItem && interliningItem.draperyOrderLiningsFabric && interliningItem.draperyOrderLiningsFabric.yardsNeeded

    const pleatTypeName = getPleatTypeName();
    const controlTypeLocation = getControlInfo();
    const { headingType } = data.draperyOrderDesign;
    let headingText = "";
    if (headingType === "rodandrings") {
        headingText = "Pleated";
    } else if (headingType === "ripplefold") {
        headingText = "Ripplefold";
    }
    const shouldShowDraperyTypeLabel =
        (data.draperyOrderDesign.draperyType === "full-length" ||
            data.draperyOrderDesign.draperyType === "cafe-curtain") &&
        data.draperyOrderDesign.headingType !== "ripplefold";
    const headingSegments: React.ReactNode[] = [];
    if (headingText) {
        headingSegments.push(
            <span key="headingText" className="boldText">
                {headingText}
            </span>
        );
    }
    if (pleatTypeName) {
        headingSegments.push(
            <span
                key="pleatType"
                className="boldText"
                style={{ textTransform: "capitalize" }}
            >
                {pleatTypeName}
            </span>
        );
    }
    if (shouldShowDraperyTypeLabel) {
        headingSegments.push(
            <span key="draperyType" className="boldText">
                {data.draperyOrderDesign.draperyType === "full-length" ? "Full Length" : "Cafe Curtain"}
            </span>
        );
    }
    const shouldShowFinishedLength =
        !!(data.draperyOrderDesign.finishedLengthOfPanels && data.draperyOrderDesign.finishedLengthOfPanels > 0);
    const shouldShowReturnSize = !!(returnSize);
    const shouldRenderMeasurementsSpacer = shouldShowFinishedLength || shouldShowReturnSize;
    const shouldInsertPostFullnessBreak = !!(
        data.draperyOrderDesign.category === 0 &&
        draperyFullness &&
        !shouldRenderMeasurementsSpacer
    );
    const shouldInsertPostFunctionalitySpacer = !!(
        data.draperyOrderDesign.category === 1 &&
        (!(panelValues && panelValues.length > 0) &&
            !shouldShowFinishedLength &&
            !shouldShowReturnSize)
    );
    const hasRodWidthSection = data.draperyOrderDesign.category === 0 && rodWidth;
    const hasFullnessSection = data.draperyOrderDesign.category === 0 && draperyFullness;
    const shouldInsertPostPanelWidthsSpacer = !!(
        panelValues && panelValues.length > 0 &&
        !hasRodWidthSection &&
        !hasFullnessSection &&
        !shouldShowFinishedLength &&
        !shouldShowReturnSize
    );

    if (!isDraperyOrder) {
        return (
            <div>
                <div>
                    <span className="boldText">Description: </span>
                    <span className="nonBold">
                        {data.draperyOrderDesign.description || ''}
                    </span>
                </div>
                {/* {isSettingEnabled("salesifliningandinterliningselected") && ( */}
                <>
                    {liningOrderOrigin === 2 && 'COM '}
                    <span className="boldText">
                        Lining:
                    </span>
                    <span style={{ paddingLeft: "8px", fontWeight: "400" }}>
                        {liningItem ? "Yes" : "No"}{liningItemName && liningItemName !== '' ? `, ${liningItemName}` : ''}{liningYardsNeeded && liningYardsNeeded !== '' ? `, ${liningYardsNeeded} yards` : ''}
                    </span>
                    <br />
                    {interliningOrderOrigin === 2 && 'COM '}
                    <span className="boldText">
                        Interlining:
                    </span>
                    <span style={{ paddingLeft: "8px", fontWeight: "400" }}>
                        {interliningItem ? "Yes" : "No"}{interliningItemName && interliningItemName !== '' ? `, ${interliningItemName}` : ''}{interliningYardsNeeded && interliningYardsNeeded !== '' ? `, ${interliningYardsNeeded} yards` : ''}
                    </span>
                    <br />
                </>
                {/* )} */}
            </div>
        );
    }

    return (
        <>

            {/* {isSettingEnabled("salesheadingandpleattype") && ( */}
            {headingSegments.length > 0 && (
                <>
                    {headingSegments.map((segment, index) => (
                        <React.Fragment key={`heading-segment-${index}`}>
                            {index > 0 && <span className="boldText"> / </span>}
                            {segment}
                        </React.Fragment>
                    ))}
                    <br />
                </>
            )}
            {/* )} */}

            {controlTypeLocation &&
                <>
                    <span>{controlTypeLocation}</span>
                    <br />
                </>
            }

            {/* {isSettingEnabled("salesfunctionalityandstyle") && ( */}
            <>
                {data.draperyOrderDesign.category === 0 && <span className="boldText">Functional</span>}
                {data.draperyOrderDesign.category === 1 && <span className="boldText">Stationary</span>}
                {
                    <span className="boldText">
                        {(data.draperyOrderDesign.category === 0 || data.draperyOrderDesign.category === 1) && displayPanelType ? <span> / </span> : ''}
                        {displayPanelType}
                    </span>
                }
            </>
            {/* )} */}

            {shouldInsertPostFunctionalitySpacer && <br />}

            {panelValues && panelValues.length > 0 &&
                <>
                    <br />
                    <span className="boldText">Panel Widths:</span>
                    <span style={{ paddingLeft: "8px", fontWeight: "400" }}>
                        {panelValues.map(value => value + "w").join(" + ")}
                    </span>
                </>
            }

            {shouldInsertPostPanelWidthsSpacer && <br />}

            {hasRodWidthSection &&
                <>
                    <br />
                    <span>Rod Width: {rodWidth}"</span>
                </>
            }
            {hasFullnessSection &&
                <>
                    {!rodWidth && <br />}
                    {hasRodWidthSection ? <span> / </span> : ''}
                    <span>Fullness: {draperyFullness}</span>
                    {shouldInsertPostFullnessBreak && <br />}
                </>
            }

            {/* {isSettingEnabled("salesfinishedlength") && data.draperyOrderDesign.finishedLengthOfPanels && data.draperyOrderDesign.finishedLengthOfPanels > 0 && ( */}
            {shouldShowFinishedLength &&
                <>
                    {/* {isSettingEnabled("salesfunctionalityandstyle") &&  */}
                    <br className="boldText"></br>
                    {/* } */}
                    <span className="nowrap">
                        <span className="boldText">Finished Length: </span>
                        <span className="nonBold">{data.draperyOrderDesign.finishedLengthOfPanels && data.draperyOrderDesign.finishedLengthOfPanels !== -1 ? data.draperyOrderDesign.finishedLengthOfPanels : '0'}"</span>
                    </span>
                </>
            }
            {/* )} */}

            {shouldShowReturnSize &&
                <>
                    {shouldShowFinishedLength ? <span> / </span> : <br />}
                    <span>Return: {returnSize}"</span>
                </>
            }

            {shouldRenderMeasurementsSpacer && <br />}

            <>
                {liningOrderOrigin === 2 && 'COM '}
                <span className="boldText">
                    Lining:
                </span>
                <span style={{ paddingLeft: "8px", fontWeight: "400" }}>
                    {liningItem ? "Yes" : "No"}{liningItemName && liningItemName !== '' ? `, ${liningItemName}` : ''}{liningYardsNeeded && liningYardsNeeded !== '' ? `, ${liningYardsNeeded} yards` : ''}
                </span>
                <br />
                {interliningOrderOrigin === 2 && 'COM '}
                <span className="boldText">
                    Interlining:
                </span>
                <span style={{ paddingLeft: "8px", fontWeight: "400" }}>
                    {interliningItem ? "Yes" : "No"}{interliningItemName && interliningItemName !== '' ? `, ${interliningItemName}` : ''}{interliningYardsNeeded && interliningYardsNeeded !== '' ? `, ${interliningYardsNeeded} yards` : ''}
                </span>
                <br />
            </>
            {/* )} */}
        </>
    );
};
